import { Card, Text, Button, BlockStack, Box, InlineStack, Tabs, TabProps } from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { SubscriptionData, BillingPeriod, SubscriptionMode, AvailableSubscriptionPlan } from "../types";
import type { PlanPricing } from "../types";
import { TranslateLink } from "@shared/ui";
import { useState } from "react";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";

const getPeriodTranslationKey = (period: BillingPeriod): string => {
  const periodKeyMap: Record<BillingPeriod, string> = {
    [BillingPeriod.MONTHLY]: "settings.subscription.period.month",
    [BillingPeriod.ANNUAL]: "settings.subscription.period.year",
  };
  return periodKeyMap[period] || `settings.subscription.period.${period}`;
};

interface AvailablePlansCardProps {
  data: SubscriptionData;
  mode: SubscriptionMode;
  clinicId: string;
}

export function AvailablePlansCard({ data, mode, clinicId }: AvailablePlansCardProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { POST } = useApiRequest();
  
  // State for selected billing period
  const [selectedBillingPeriod, setSelectedBillingPeriod] = useState<BillingPeriod>(
    data.currentSubscription.billingPeriod || BillingPeriod.MONTHLY
  );

  // Subscription mutation
  type SubscriptionResponse = { data?: { link?: string } };
  const subscriptionMutation = useMutation({
    mutationFn: (planId: string) => {
      return POST<SubscriptionResponse>(
        `/api/healthcare-companies/${clinicId}/billing/subscription`,
        {
          productId: planId,
          billingInterval: selectedBillingPeriod,
          redirectURI: window.location.href,
          productType: "subscription",
        }
      );
    },
    onSuccess: (response) => {
      // The API returns a redirect link in data.link
      if (response?.data?.link) {
        window.location.href = response.data.link;
      }
      // Also invalidate queries in case the redirect doesn't happen immediately
      queryClient.invalidateQueries({ 
        queryKey: ["subscription", clinicId] 
      });
    },
  });

  // Tabs configuration
  const tabs: TabProps[] = [
    {
      id: BillingPeriod.MONTHLY,
      content: t("settings.subscription.payMonthly", { defaultValue: "Pay monthly" }),
    },
    {
      id: BillingPeriod.ANNUAL,
      content: t("settings.subscription.payYearly", { defaultValue: "Pay yearly" }),
      badge: t("settings.subscription.save25", { defaultValue: "Save 25%" }),
    },
  ];

  const selectedTabIndex = selectedBillingPeriod === BillingPeriod.MONTHLY ? 0 : 1;

  const handleTabChange = (selectedIndex: number) => {
    const newBillingPeriod = selectedIndex === 0 ? BillingPeriod.MONTHLY : BillingPeriod.ANNUAL;
    setSelectedBillingPeriod(newBillingPeriod);
  };

  // Helper function to get pricing for selected billing period
  const getPricingForPeriod = (plan: AvailableSubscriptionPlan) => {
    return plan.pricing.find((p: PlanPricing) => p.period === selectedBillingPeriod) || plan.pricing[0];
  };

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack gap="400" align="space-between">
          <Text variant="headingMd" as="h2">
            {mode === SubscriptionMode.FREE
              ? t("settings.subscription.choosePlan")
              : t("settings.subscription.upgradePlan")}
          </Text>
          <Tabs
            fitted={true}
            tabs={tabs} 
            selected={selectedTabIndex} 
            onSelect={handleTabChange} 
          />
        </InlineStack>
        
        {data.subscriptionPlans.map((plan: AvailableSubscriptionPlan) => {
          const pricing = getPricingForPeriod(plan);
          
          return (
            <div key={plan.id}>
              <Card>
                <BlockStack gap="400">
                  <InlineStack gap="200" align="space-between">
                    <Text variant="headingLg" as="h3">
                      {plan.label}
                    </Text>
                    {plan.isPopular && (
                      <Text variant="bodyMd" as="span" tone="success">
                        {t("settings.subscription.popular", { defaultValue: "Popular" })}
                      </Text>
                    )}
                  </InlineStack>
                  
                  <InlineStack gap="100" blockAlign="baseline">
                    <Text variant="headingLg" as="p">
                      {pricing.priceLabel}
                    </Text>
                    <Text variant="bodySm" as="span" tone="subdued">
                      {t(getPeriodTranslationKey(pricing.period))}
                    </Text>
                  </InlineStack>
                  
                  <TranslateLink text={plan.description} />
                  
                  {plan.patientLimit && (
                    <Text variant="bodySm" as="p" tone="subdued">
                      {t("settings.subscription.patientLimit", { 
                        count: plan.patientLimit,
                        defaultValue: "Up to {{count}} patients" 
                      })}
                    </Text>
                  )}
                  
                  {plan.cta && !plan.isCurrent && (
                    <Box>
                      <Button
                        variant="primary"
                        size="large"
                        loading={subscriptionMutation.isPending}
                        onClick={() => subscriptionMutation.mutate(plan.id)}
                      >
                        {plan.cta.label}
                      </Button>
                    </Box>
                  )}
                  
                  {plan.isCurrent && (
                    <Box>
                      <Button
                        variant="secondary"
                        size="large"
                        disabled
                      >
                        {t("settings.subscription.currentPlan", { defaultValue: "Current Plan" })}
                      </Button>
                    </Box>
                  )}
                </BlockStack>
              </Card>
            </div>
          );
        })}
      </BlockStack>
    </Card>
  );
}
